// Spec §2.5 Tab 4 "Real-time map (all active driver locations)".
// Composes:
//   - IN_PROGRESS routes' stop targets via DeliveryRouteApi.list
//   - Latest GPS points per active DN via MetricsApi.driverLocations
// RouteMap renders both layers (stop markers + driver pucks) on one
// Leaflet canvas. 10s refresh on the GPS feed; 30s on the route stops.

import { Badge, Box, Group, Loader, Stack, Text } from "@mantine/core"
import useSWR from "swr"
import { DeliveryRouteApi, MetricsApi } from "../../apis"
import type { IDeliveryRoute, IRouteStop } from "../../apis/delivery-route"
import { RouteMap } from "../delivery-routes"

export function ActiveDeliveriesMap() {
  const { data: routes, isLoading: routesLoading } = useSWR(
    "active-deliveries.in-progress-routes",
    async () => {
      const res = await DeliveryRouteApi.list({
        filter: { status: "IN_PROGRESS" },
        offset: { limit: 50, skip: 0 },
      })
      return res.rows ?? []
    },
    { revalidateOnFocus: false, refreshInterval: 30000 },
  )

  // GPS feed — separate, shorter refresh so the puck moves visibly.
  const { data: locFeed } = useSWR(
    "active-deliveries.driver-locations",
    () => MetricsApi.driverLocations({ freshMinutes: 10 }),
    { revalidateOnFocus: false, refreshInterval: 10000 },
  )

  const stops: IRouteStop[] = (routes ?? []).flatMap((r: IDeliveryRoute) => r.stops ?? [])
  const pucks = locFeed?.rows ?? []

  if (routesLoading) {
    return (
      <Box h={320} style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: "var(--mantine-radius-md)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Loader size="sm" />
      </Box>
    )
  }

  if (stops.length === 0 && pucks.length === 0) {
    return (
      <Stack h={320} align="center" justify="center" style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: "var(--mantine-radius-md)", background: "var(--mantine-color-gray-0)" }}>
        <Text size="sm" c="dimmed">Идэвхтэй маршрут байхгүй</Text>
      </Stack>
    )
  }

  return (
    <Stack gap="xs">
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <Badge variant="light" color="brand" size="sm">{stops.length} зогсоол</Badge>
          <Badge variant="dot" color="green" size="sm">{pucks.length} жолооч</Badge>
        </Group>
        <Text size="sm" c="dimmed">10 сек тутамд шинэчлэгдэнэ</Text>
      </Group>
      <RouteMap stops={stops} height={320} driverPucks={pucks} />
    </Stack>
  )
}
